import { useAuth } from '../context/AuthContext';
import { useParams } from 'react-router-dom';

/**
 * Custom hook for handling user permissions
 * Centralizes all permission checking logic for sub-users
 */
export const usePermissions = () => {
  const { currentUser } = useAuth();
  const { projectId } = useParams();

  /**
   * Check if current user is a sub-user
   */
  const isSubUser = () => {
    return !!currentUser?.isSubUser;
  };

  /**
   * Check if current user is a main user (not sub-user)
   */
  const isMainUser = () => {
    return !currentUser?.isSubUser;
  };

  /**
   * Get permissions for the current project
   */
  const getCurrentProjectPermissions = () => {
    if (!isSubUser() || !projectId) return null;
    
    const accessibleProject = currentUser.accessibleProjects?.find(
      ap => (ap.project._id || ap.project) === projectId
    );
    
    return accessibleProject?.permissions || {};
  };

  /**
   * Check if user has a specific permission for current project
   */
  const hasPermission = (permission) => {
    // Main users have all permissions
    if (isMainUser()) return true;
    
    const permissions = getCurrentProjectPermissions();
    return permissions ? !!permissions[permission] : false;
  };

  /**
   * Check if user has view access to a section
   */
  const canView = (section) => {
    const permissionMap = {
      dashboard: 'viewDashboard',
      analytics: 'viewAnalytics',
      apiEndpoints: 'viewApiEndpoints',
      knowledgeBase: 'viewKnowledgeBase',
      flowBuilder: 'viewFlowBuilder',
      humanAgents: 'viewHumanAgents',
      locations: 'viewLocations',
      settings: 'viewSettings',
      sdkIntegration: 'viewSDKIntegration'
    };

    return hasPermission(permissionMap[section]);
  };

  /**
   * Check if user has manage/edit access to a section
   */
  const canManage = (section) => {
    const permissionMap = {
      project: 'editProject',
      apiEndpoints: 'manageApiEndpoints',
      knowledgeBase: 'manageKnowledgeBase',
      flowBuilder: 'manageFlowBuilder',
      humanAgents: 'manageHumanAgents',
      locations: 'manageLocations',
      settings: 'manageSettings',
      sdkIntegration: 'manageSDKIntegration'
    };

    return hasPermission(permissionMap[section]);
  };

  /**
   * Check if user can access sensitive sections (API keys, billing, etc.)
   * Sub-users should never have access to these
   */
  const canAccessSensitive = () => {
    return isMainUser();
  };

  /**
   * Check if user can create projects
   */
  const canCreateProjects = () => {
    return isMainUser();
  };

  /**
   * Check if user can generate reports
   */
  const canGenerateReports = () => {
    return isMainUser();
  };

  /**
   * Check if user can sync to production
   */
  const canSyncToProduction = () => {
    return isMainUser();
  };

  /**
   * Check if user can test chatbot
   */
  const canTestChatbot = () => {
    return hasPermission('testChatbot');
  };

  /**
   * Get user access level for current project
   * Returns: 'none', 'read', 'manage', 'full'
   */
  const getAccessLevel = (section) => {
    if (isMainUser()) return 'full';
    if (!canView(section)) return 'none';
    if (canManage(section)) return 'manage';
    return 'read';
  };

  /**
   * Check if user has access to billing information
   */
  const canViewBilling = () => {
    return isMainUser();
  };

  /**
   * Check if user can edit project settings
   */
  const canEditProject = (projectId) => {
    return canManage('project');
  };

  return {
    isSubUser: isSubUser(),
    isMainUser: isMainUser(),
    getCurrentProjectPermissions,
    hasPermission,
    canView,
    canManage,
    canAccessSensitive,
    canCreateProjects,
    canGenerateReports,
    canSyncToProduction,
    canTestChatbot,
    canViewBilling,
    canEditProject,
    getAccessLevel,
  };
};

export default usePermissions; 